import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import UpcomingRecurrentTransactionsCarousel from "../../../../../components/ui/carousels/UpcomingRecurrentTransactionsCarousel";
import DataTable from "../../../../../components/ui/table/DataTable";
import { getRecurringTransactions } from "../../../../../api/finances/recurringTransactionsAPI";
import { useContentHeightCalculation } from "../../../../../hooks/useContentHeightCalculation";
import PersonalFinancesMenu from "./common/PersonalFinancesMenu";
import FilterModal from "../../../../../components/ui/popup/FilterModal";
import RecurringDetailsModal from "../../../../../components/ui/popup/finances/RecurringDetailsModal";
import {
  getUniqueOptions,
  mapRecurringTransactionToRow,
} from "../../../../../components/ui/popup/finances/utils/helpers";
import LoadingPlaceholder from "../../../../../components/ui/loading/LoadingPlaceholder";
import GhostCardContainer from "../../../../../components/ui/loading/skeletons/GhostCardContainer";
import GhostDataTable from "../../../../../components/ui/loading/skeletons/GhostDataTable";
import { useRefreshOnAuth } from "../../../../../hooks/useRefreshOnAuth";
import LoadingToRealTransitionWrapper from "../../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import InstitutionDataLoaderBanner from "./common/InsitutionDataLoaderBanner";
import InstitutionPartialDataWarningBanner from "./common/InstitutionPartialDataWarningBanner";
import { getLanguageContent } from "../../../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../../../utils/schemas/language/pages/personalFinancesSchema";
import { FILTER_SCHEMA } from "../../../../../utils/schemas/language/components/filterSchema";

const SEARCH_HEADERS = ["merchant"];

function RecurringPersonalTransactions() {
  const content = getLanguageContent(PERSONAL_FINANCES_SCHEMA);
  const filterContent = getLanguageContent(FILTER_SCHEMA);
  const { contentRef, mainRef } = useContentHeightCalculation();

  // ============================================================
  // PIPELINE SYNC STATE EXTRACTION & CORRECTED EVALUATIONS
  // ============================================================
  const {
    institutionSyncStates = [],
    loadSyncStates,
    isLoadingSyncStates,
  } = useOutletContext() || {};

  // Rule 1: Fallback to onboarding card loader if every account has a completely blank subscriptions snapshot
  const isEmptyOnboarding = useMemo(() => {
    if (isLoadingSyncStates) return true;
    if (institutionSyncStates.length === 0) return false;
    return institutionSyncStates.every(
      (inst) => inst.recurring_object_completed === false,
    );
  }, [institutionSyncStates, isLoadingSyncStates]);

  // Rule 2: Activate banner if initial subscription maps exist, but background processing is running
  const isPartialSyncing = useMemo(() => {
    if (
      isLoadingSyncStates ||
      isEmptyOnboarding ||
      institutionSyncStates.length === 0
    ) {
      return false;
    }
    return institutionSyncStates.some(
      (inst) =>
        inst.recurring_object_completed === false ||
        inst.transaction_history_completed === false,
    );
  }, [institutionSyncStates, isEmptyOnboarding, isLoadingSyncStates]);

  // Fixed: Programmatic text generator capturing any running historical sync pipeline or subscription pass
  const processingInstitutionsText = useMemo(() => {
    const pendingNames = institutionSyncStates
      .filter(
        (inst) =>
          inst.recurring_object_completed === false ||
          inst.transaction_history_completed === false,
      )
      .map((inst) => inst.institution_name)
      .filter(Boolean);

    if (pendingNames.length === 0) return "";
    if (pendingNames.length === 1) return pendingNames[0];
    if (pendingNames.length === 2)
      return `${pendingNames[0]} & ${pendingNames[1]}`;
    return `${pendingNames.slice(0, -1).join(", ")}, & ${
      pendingNames[pendingNames.length - 1]
    }`;
  }, [institutionSyncStates]);

  const [recurringRows, setRecurringRows] = useState([]);
  const [merchantSearch, setMerchantSearch] = useState("");
  const [isLoadingRecurring, setIsLoadingRecurring] = useState(true);
  const [recurringError, setRecurringError] = useState("");

  const headers = [
    {
      label: content.recurring.headers.merchant,
      key: "merchant",
      sortable: false,
      width: "200px",
      showAmount: true,
    },
    {
      label: content.recurring.headers.type,
      key: "recurrent_type",
      sortable: false,
      width: "200px",
    },
    {
      label: content.recurring.headers.next_bill,
      key: "date",
      sortable: true,
      width: "200px",
    },
    {
      label: content.recurring.headers.frequency,
      key: "frequency",
      sortable: false,
      width: "200px",
    },
  ];

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState({
    institution_uuid: "",
    account_uuid: "",
    category: "",
    recurrent_type: "",
  });
  const [activeFilters, setActiveFilters] = useState({
    institution_uuid: "",
    account_uuid: "",
    category: "",
    recurrent_type: "",
  });

  const loadRecurringTransactions = useCallback(async () => {
    setIsLoadingRecurring(true);
    setRecurringError("");
    try {
      const data = await getRecurringTransactions({ all: true });
      const allRows = [
        ...(data?.all?.outflows || []),
        ...(data?.all?.inflows || []),
      ]
        .map(mapRecurringTransactionToRow)
        .sort(
          (firstRow, secondRow) =>
            Date.parse(firstRow.date_value) - Date.parse(secondRow.date_value),
        );
      setRecurringRows(allRows);
    } catch (error) {
      setRecurringError(
        "We could not load your recurring transactions. Please try again.",
      );
    } finally {
      setIsLoadingRecurring(false);
    }
  }, []);

  useRefreshOnAuth(loadRecurringTransactions);

  useEffect(() => {
    loadRecurringTransactions();
  }, [loadRecurringTransactions]);

  const filteredRows = useMemo(() => {
    return recurringRows.filter((row) => {
      return (
        (!activeFilters.institution_uuid ||
          row.institution_uuid === activeFilters.institution_uuid) &&
        (!activeFilters.account_uuid ||
          row.account_uuid === activeFilters.account_uuid) &&
        (!activeFilters.category ||
          row.category_value === activeFilters.category) &&
        (!activeFilters.recurrent_type ||
          row.recurrent_type === activeFilters.recurrent_type)
      );
    });
  }, [activeFilters, recurringRows]);

  const getRowsForFilterOptions = useCallback(
    (excludedField) => {
      return recurringRows.filter((row) => {
        return (
          (excludedField === "institution_uuid" ||
            !draftFilters.institution_uuid ||
            row.institution_uuid === draftFilters.institution_uuid) &&
          (excludedField === "account_uuid" ||
            !draftFilters.account_uuid ||
            row.account_uuid === draftFilters.account_uuid) &&
          (excludedField === "category" ||
            !draftFilters.category ||
            row.category_value === draftFilters.category) &&
          (excludedField === "recurrent_type" ||
            !draftFilters.recurrent_type ||
            row.recurrent_type === draftFilters.recurrent_type)
        );
      });
    },
    [draftFilters, recurringRows],
  );

  const institutionOptions = useMemo(
    () =>
      getUniqueOptions(
        getRowsForFilterOptions("institution_uuid"),
        "institution_uuid",
        "institution",
      ),
    [getRowsForFilterOptions],
  );
  const categoryOptions = useMemo(
    () =>
      getUniqueOptions(
        getRowsForFilterOptions("category"),
        "category_value",
        "category",
      ),
    [getRowsForFilterOptions],
  );
  const recurrentTypeOptions = useMemo(
    () =>
      getUniqueOptions(
        getRowsForFilterOptions("recurrent_type"),
        "recurrent_type",
      ),
    [getRowsForFilterOptions],
  );
  const accountOptions = useMemo(
    () =>
      getUniqueOptions(
        getRowsForFilterOptions("account_uuid"),
        "account_uuid",
        "account",
      ),
    [getRowsForFilterOptions],
  );

  const handleFilterChange = (field, value) => {
    setDraftFilters((currentFilters) => {
      const nextFilters = {
        ...currentFilters,
        [field]: value,
        ...(field === "institution_uuid" ? { account_uuid: "" } : {}),
      };
      const matchesFilters = (candidateFilters) =>
        recurringRows.some((row) => {
          return (
            (!candidateFilters.institution_uuid ||
              row.institution_uuid === candidateFilters.institution_uuid) &&
            (!candidateFilters.account_uuid ||
              row.account_uuid === candidateFilters.account_uuid) &&
            (!candidateFilters.category ||
              row.category_value === candidateFilters.category) &&
            (!candidateFilters.recurrent_type ||
              row.recurrent_type === candidateFilters.recurrent_type)
          );
        });

      if (matchesFilters(nextFilters)) return nextFilters;
      const staleFields = ["account_uuid", "category", "recurrent_type"].filter(
        (f) => f !== field,
      );
      return staleFields.reduce((acc, f) => ({ ...acc, [f]: "" }), nextFilters);
    });
  };

  const handleOpenFilters = () => {
    setDraftFilters(activeFilters);
    setIsFilterOpen(true);
  };
  const handleCloseFilters = () => {
    setDraftFilters(activeFilters);
    setIsFilterOpen(false);
  };
  const handleApplyFilters = () => {
    setActiveFilters(draftFilters);
    setIsFilterOpen(false);
  };

  const handleRecurringTransactionUpdate = (updatedTransaction) => {
    setSelectedTransaction(updatedTransaction);
    setRecurringRows((currentRows) =>
      currentRows.map((row) =>
        row.uuid === updatedTransaction.uuid
          ? { ...row, ...updatedTransaction }
          : row,
      ),
    );
  };

  const searchRightElement = (
    <div
      className="search-input-element"
      style={{ position: "relative", display: "flex", alignItems: "center" }}
    >
      <div className="form-field" style={{ flex: 1 }}>
        <input
          className="form-input"
          type="text"
          placeholder={content.recurring.search_placeholder}
          value={merchantSearch}
          onChange={(e) => setMerchantSearch(e.target.value)}
        />
      </div>
      <img
        src={`${process.env.PUBLIC_URL}/images/icons/search_blue.svg`}
        alt=""
        style={{
          position: "absolute",
          right: "10px",
          width: "22px",
          pointerEvents: "none",
        }}
      />
    </div>
  );

  // Interceptor Gate 1: Full Block Loading Card
  if (isEmptyOnboarding) {
    return (
      <InstitutionDataLoaderBanner
        content={content.status.recurring.no_data}
        refreshContent={content.global.refresh_in}
        onRefresh={loadSyncStates}
        hiddeRefresh={true}
      />
    );
  }

  return (
    <Fragment>
      {/* Interceptor Gate 2: Partial Warning Alert Banner */}
      {isPartialSyncing && (
        <InstitutionPartialDataWarningBanner
          onRefresh={loadSyncStates}
          content={content.status.recurring.processing}
          processingText={processingInstitutionsText}
          refreshText={content.global.refresh}
        />
      )}

      <>
        {recurringError && (
          <p className="text-danger" style={{ marginBottom: "12px" }}>
            {recurringError}
          </p>
        )}

        <UpcomingRecurrentTransactionsCarousel />

        <LoadingToRealTransitionWrapper
          isLoading={isLoadingRecurring}
          ghost={
            <GhostCardContainer>
              <GhostDataTable
                headers={headers}
                rows={2}
                title={true}
                description={true}
                showSearch={true}
              />
            </GhostCardContainer>
          }
        >
          <div className="mobile-white-box">
            <DataTable
              title={content.recurring.table_title}
              headers={headers}
              rows={filteredRows}
              getActionMenu={(row) => [
                {
                  label: content.recurring.actions.view_edit,
                  onClick: () => {
                    setSelectedTransaction(row);
                    setIsModalOpen(true);
                  },
                },
              ]}
              rightElement={searchRightElement}
              filterElement={true}
              onFilterClick={handleOpenFilters}
              searchQuery={merchantSearch}
              searchHeaders={SEARCH_HEADERS}
              emptyStateTitle={content.recurring.empty}
              emptyStateText={content.recurring.empty}
            />
          </div>
        </LoadingToRealTransitionWrapper>
      </>

      <FilterModal
        isOpen={isFilterOpen}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        title={content.recurring.filter_title}
        applyText={filterContent.apply}
      >
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.institution_uuid}
            onChange={(e) =>
              handleFilterChange("institution_uuid", e.target.value)
            }
          >
            <option value="">{filterContent.all_institutions}</option>
            {institutionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.account_uuid}
            onChange={(e) => handleFilterChange("account_uuid", e.target.value)}
          >
            <option value="">{filterContent.all_accounts}</option>
            {accountOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.category}
            onChange={(e) => handleFilterChange("category", e.target.value)}
          >
            <option value="">{filterContent.all_categories}</option>
            {categoryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.recurrent_type}
            onChange={(e) =>
              handleFilterChange("recurrent_type", e.target.value)
            }
          >
            <option value="">{filterContent.all_types}</option>
            {recurrentTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </FilterModal>

      {isModalOpen && (
        <RecurringDetailsModal
          transaction={selectedTransaction}
          onTransactionUpdate={handleRecurringTransactionUpdate}
          onClose={() => setIsModalOpen(false)}
          copy={content.recurring.details}
          commonCopy={content.global}
        />
      )}
    </Fragment>
  );
}

export default RecurringPersonalTransactions;
